import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { candidateService } from '../services/candidateService';
import {
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
  CButton, CFormInput, CFormSelect, CFormTextarea, CFormLabel,
  CRow, CCol, CNav, CNavItem, CNavLink, CTabContent, CTabPane,
} from '@coreui/react';

const MAPPING_OPTIONS = [
  { value: 'new', label: 'New' }, { value: 'screening', label: 'Screening' },
  { value: 'interview', label: 'Interview' }, { value: 'offer', label: 'Offer' },
  { value: 'hired', label: 'Hired' }, { value: 'rejected', label: 'Rejected' },
  { value: 'pool', label: 'Pool' },
];
const OFFER_OPTIONS = [
  { value: '', label: 'None' }, { value: 'pending', label: 'Pending' },
  { value: 'offered', label: 'Offered' }, { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' }, { value: 'withdrawn', label: 'Withdrawn' },
];
const PREFIX_OPTIONS = ['Mr.', 'Ms.', 'Mrs.', 'Dr.'];
const EDUCATION_OPTIONS = ['High School', 'Vocational', 'Diploma', 'Bachelor', 'Master', 'Doctorate'];
const SCORE_FIELDS = [
  { key: 'english', label: 'English (20)' },
  { key: 's1_building_manager', label: 'S1 Building Mgr (20)' },
  { key: 's2_village_manager', label: 'S2 Village Mgr (20)' },
  { key: 's3_technician', label: 'S3 Technician (20)' },
  { key: 's4_head_technician', label: 'S4 Head Tech (20)' },
  { key: 's5_admin', label: 'S5 Admin (20)' },
  { key: 's6_rm', label: 'S6 RM (20)' },
  { key: 's7', label: 'S7 (20)' },
];
const DEFAULT_SCORES = { english: 0, s1_building_manager: 0, s2_village_manager: 0, s3_technician: 0, s4_head_technician: 0, s5_admin: 0, s6_rm: 0, s7: 0 };
const DEFAULT_FORM = {
  prefix: '', first_name: '', last_name: '', nickname: '', age: '', phone: '',
  position_id: '', resume_url: '', email: '', interview_result: '', current_address: '',
  preferred_work_zone: '', education_level: '', last_salary: '', expected_salary: '',
  available_start_date: '', building_management_experience: '', interview_round: '',
  job_posting_channel: '', mapping_status: 'new', project_id: '', offer_status: '', reason: '',
  scores: { ...DEFAULT_SCORES },
};

function CandidateFormDialog({ open, onClose, onSave, candidate, positions, projects }) {
  const { authAxios } = useAuth();
  const [form, setForm] = useState({ ...DEFAULT_FORM });
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('personal');

  useEffect(() => {
    if (candidate) {
      setForm({
        prefix: candidate.prefix || '', first_name: candidate.first_name || '', last_name: candidate.last_name || '',
        nickname: candidate.nickname || '', age: candidate.age || '', phone: candidate.phone || '',
        position_id: candidate.position_id || '', resume_url: candidate.resume_url || '', email: candidate.email || '',
        interview_result: candidate.interview_result || '', current_address: candidate.current_address || '',
        preferred_work_zone: candidate.preferred_work_zone || '', education_level: candidate.education_level || '',
        last_salary: candidate.last_salary || '', expected_salary: candidate.expected_salary || '',
        available_start_date: candidate.available_start_date || '',
        building_management_experience: candidate.building_management_experience || '',
        interview_round: candidate.interview_round || '', job_posting_channel: candidate.job_posting_channel || '',
        mapping_status: candidate.mapping_status || 'new', project_id: candidate.project_id || '',
        offer_status: candidate.offer_status || '', reason: candidate.reason || '',
        scores: candidate.scores || { ...DEFAULT_SCORES },
      });
    } else {
      setForm({ ...DEFAULT_FORM, scores: { ...DEFAULT_SCORES } });
    }
    setTab('personal');
  }, [candidate, open]);

  const updateField = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const updateScore = (key, val) => {
    const num = Math.min(Math.max(parseInt(val) || 0, 0), 20);
    setForm(prev => ({ ...prev, scores: { ...prev.scores, [key]: num } }));
  };

  const handleSubmit = async () => {
    if (!form.first_name || !form.last_name) { toast.error('First name and last name are required'); return; }
    setSaving(true);
    try {
      const api = authAxios();
      const payload = {
        ...form,
        age: form.age ? parseInt(form.age) : null,
        last_salary: form.last_salary ? parseFloat(form.last_salary) : null,
        expected_salary: form.expected_salary ? parseFloat(form.expected_salary) : null,
      };
      if (candidate) {
        await candidateService.update(api, candidate.id, payload);
        toast.success('Candidate updated');
      } else {
        await candidateService.create(api, payload);
        toast.success('Candidate added');
      }
      onSave();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const tabs = [
    { id: 'personal', label: 'Personal' },
    { id: 'job', label: 'Job Info' },
    { id: 'scores', label: 'Scores' },
    { id: 'status', label: 'Status' },
  ];

  return (
    <CModal visible={open} onClose={onClose} size="lg" data-testid="candidate-form-dialog">
      <CModalHeader>
        <CModalTitle className="fw-bold">{candidate ? 'Edit Candidate' : 'Add New Candidate'}</CModalTitle>
      </CModalHeader>
      <CModalBody style={{ maxHeight: '65vh', overflowY: 'auto' }}>
        <p className="small text-body-secondary mb-3">S1-S7 scores at 16+ (80%) auto-check as passed.</p>

        <CNav variant="tabs" className="mb-3">
          {tabs.map(t => (
            <CNavItem key={t.id}>
              <CNavLink active={tab === t.id} onClick={() => setTab(t.id)} role="button" data-testid={'form-tab-' + t.id}>
                {t.label}
              </CNavLink>
            </CNavItem>
          ))}
        </CNav>

        <CTabContent>
          <CTabPane visible={tab === 'personal'}>
            <CRow className="g-2 mb-2">
              <CCol sm={3}>
                <CFormLabel className="small">Prefix</CFormLabel>
                <CFormSelect size="sm" value={form.prefix || 'Mr.'} onChange={e => updateField('prefix', e.target.value)} data-testid="form-prefix">
                  {PREFIX_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                </CFormSelect>
              </CCol>
              <CCol sm={3}>
                <CFormLabel className="small">First Name *</CFormLabel>
                <CFormInput size="sm" value={form.first_name} onChange={e => updateField('first_name', e.target.value)} data-testid="form-first-name" />
              </CCol>
              <CCol sm={3}>
                <CFormLabel className="small">Last Name *</CFormLabel>
                <CFormInput size="sm" value={form.last_name} onChange={e => updateField('last_name', e.target.value)} data-testid="form-last-name" />
              </CCol>
              <CCol sm={3}>
                <CFormLabel className="small">Nickname</CFormLabel>
                <CFormInput size="sm" value={form.nickname} onChange={e => updateField('nickname', e.target.value)} data-testid="form-nickname" />
              </CCol>
            </CRow>
            <CRow className="g-2 mb-2">
              <CCol sm={4}><CFormLabel className="small">Age</CFormLabel><CFormInput size="sm" type="number" value={form.age} onChange={e => updateField('age', e.target.value)} data-testid="form-age" /></CCol>
              <CCol sm={4}><CFormLabel className="small">Phone</CFormLabel><CFormInput size="sm" value={form.phone} onChange={e => updateField('phone', e.target.value)} data-testid="form-phone" /></CCol>
              <CCol sm={4}><CFormLabel className="small">Email</CFormLabel><CFormInput size="sm" type="email" value={form.email} onChange={e => updateField('email', e.target.value)} data-testid="form-email" /></CCol>
            </CRow>
            <div className="mb-2"><CFormLabel className="small">Current Address</CFormLabel><CFormTextarea rows={2} value={form.current_address} onChange={e => updateField('current_address', e.target.value)} data-testid="form-address" /></div>
          </CTabPane>

          <CTabPane visible={tab === 'job'}>
            <CRow className="g-2 mb-2">
              <CCol sm={6}>
                <CFormLabel className="small">Position Applied</CFormLabel>
                <CFormSelect size="sm" value={form.position_id} onChange={e => updateField('position_id', e.target.value)} data-testid="form-position">
                  <option value="">Select position</option>
                  {(positions || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </CFormSelect>
              </CCol>
              <CCol sm={6}><CFormLabel className="small">Preferred Work Zone</CFormLabel><CFormInput size="sm" value={form.preferred_work_zone} onChange={e => updateField('preferred_work_zone', e.target.value)} data-testid="form-zone" placeholder="e.g. North, South" /></CCol>
            </CRow>
            <CRow className="g-2 mb-2">
              <CCol sm={6}>
                <CFormLabel className="small">Education Level</CFormLabel>
                <CFormSelect size="sm" value={form.education_level} onChange={e => updateField('education_level', e.target.value)} data-testid="form-education">
                  <option value="">Select</option>
                  {EDUCATION_OPTIONS.map(e => <option key={e} value={e}>{e}</option>)}
                </CFormSelect>
              </CCol>
              <CCol sm={6}><CFormLabel className="small">Resume/CV URL</CFormLabel><CFormInput size="sm" value={form.resume_url} onChange={e => updateField('resume_url', e.target.value)} data-testid="form-resume" /></CCol>
            </CRow>
            <CRow className="g-2 mb-2">
              <CCol sm={4}><CFormLabel className="small">Last Salary</CFormLabel><CFormInput size="sm" type="number" value={form.last_salary} onChange={e => updateField('last_salary', e.target.value)} data-testid="form-last-salary" /></CCol>
              <CCol sm={4}><CFormLabel className="small">Expected Salary</CFormLabel><CFormInput size="sm" type="number" value={form.expected_salary} onChange={e => updateField('expected_salary', e.target.value)} data-testid="form-expected-salary" /></CCol>
              <CCol sm={4}><CFormLabel className="small">Available Start Date</CFormLabel><CFormInput size="sm" type="date" value={form.available_start_date} onChange={e => updateField('available_start_date', e.target.value)} data-testid="form-start-date" /></CCol>
            </CRow>
            <CRow className="g-2 mb-2">
              <CCol sm={6}><CFormLabel className="small">Building/Village Mgmt Experience</CFormLabel><CFormInput size="sm" value={form.building_management_experience} onChange={e => updateField('building_management_experience', e.target.value)} data-testid="form-experience" /></CCol>
              <CCol sm={6}><CFormLabel className="small">Job Posting Channel</CFormLabel><CFormInput size="sm" value={form.job_posting_channel} onChange={e => updateField('job_posting_channel', e.target.value)} data-testid="form-channel" /></CCol>
            </CRow>
            <CRow className="g-2">
              <CCol sm={6}><CFormLabel className="small">Interview Round</CFormLabel><CFormInput size="sm" value={form.interview_round} onChange={e => updateField('interview_round', e.target.value)} data-testid="form-round" /></CCol>
              <CCol sm={6}><CFormLabel className="small">Interview Result</CFormLabel><CFormInput size="sm" value={form.interview_result} onChange={e => updateField('interview_result', e.target.value)} data-testid="form-interview-result" /></CCol>
            </CRow>
          </CTabPane>

          <CTabPane visible={tab === 'scores'}>
            <p className="small text-body-secondary mb-3">Enter scores out of 20. S1-S7 at 16+ = auto-pass.</p>
            <CRow className="g-2">
              {SCORE_FIELDS.map(({ key, label }) => {
                const val = form.scores?.[key] || 0;
                const isS = key !== 'english';
                return (
                  <CCol sm={6} key={key}>
                    <CFormLabel className="small">{label}</CFormLabel>
                    <div className="d-flex align-items-center gap-2">
                      <CFormInput
                        size="sm" type="number" min="0" max="20"
                        className={isS && val >= 16 ? 'border-success' : ''}
                        value={val || ''} onChange={e => updateScore(key, e.target.value)}
                        data-testid={'form-score-' + key}
                      />
                      {isS && val >= 16 && <small className="text-success fw-medium">Pass</small>}
                      {isS && val > 0 && val < 16 && <small className="text-danger fw-medium">Fail</small>}
                    </div>
                  </CCol>
                );
              })}
            </CRow>
          </CTabPane>

          <CTabPane visible={tab === 'status'}>
            <CRow className="g-2 mb-2">
              <CCol sm={6}>
                <CFormLabel className="small">Mapping Status</CFormLabel>
                <CFormSelect size="sm" value={form.mapping_status} onChange={e => updateField('mapping_status', e.target.value)} data-testid="form-mapping">
                  {MAPPING_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </CFormSelect>
              </CCol>
              <CCol sm={6}>
                <CFormLabel className="small">Offer Status</CFormLabel>
                <CFormSelect size="sm" value={form.offer_status} onChange={e => updateField('offer_status', e.target.value)} data-testid="form-offer">
                  {OFFER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </CFormSelect>
              </CCol>
            </CRow>
            <div className="mb-2">
              <CFormLabel className="small">Map to Project</CFormLabel>
              <CFormSelect size="sm" value={form.project_id} onChange={e => updateField('project_id', e.target.value)} data-testid="form-project">
                <option value="">No Project</option>
                {(projects || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </CFormSelect>
            </div>
            <div>
              <CFormLabel className="small">Reason / Notes</CFormLabel>
              <CFormTextarea rows={3} value={form.reason} onChange={e => updateField('reason', e.target.value)} data-testid="form-reason" />
            </div>
          </CTabPane>
        </CTabContent>
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" variant="ghost" onClick={onClose} data-testid="form-cancel">Cancel</CButton>
        <CButton color="primary" onClick={handleSubmit} disabled={saving} data-testid="form-save">
          {saving ? 'Saving...' : (candidate ? 'Update' : 'Add Candidate')}
        </CButton>
      </CModalFooter>
    </CModal>
  );
}

export default CandidateFormDialog;
